import {useEffect, useMemo, useCallback, useRef} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useMapStore} from '../../map/store/MapStore';
import {useStoreSelectionStore} from '../store/StoreSelectionStore';
import {Store} from '../models/store.model';
import {useAuth} from '../../auth/hooks/useAuth';
import {AuthorizationService} from '../../../Services/authorization/AuthorizationService';

/**
 * Hook for fetching stores data for map display (non-provider users)
 * @param shouldFetch - Whether to enable data fetching (default: true)
 * @returns Object containing stores data, loading state, and error
 */
export const useStoresMap = (shouldFetch: boolean = true) => {
  // Use separate stores
  const {bounds, error: mapError} = useMapStore();
  const {stores, setStores, setLoading, setError} = useStoreSelectionStore();
  const {isAuthenticated, initialized, user} = useAuth();
  const isProvider = AuthorizationService.getInstance().isServiceProvider(user);

  const debounceRef = useRef<number>();
  const lastBoundsRef = useRef<string>('');

  // Debounce function to delay data fetching
  const debouncedFetch = useCallback((_queryKey: any[], queryFn: () => Promise<any>) => {
    return new Promise((resolve, reject) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        try {
          // Add additional delay to ensure authentication is ready and reduce API calls
          await new Promise(resolve => setTimeout(resolve, 1000));
          const result = await queryFn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, 2000); // Increased debounce time to 2 seconds to reduce API calls
    });
  }, []);

  // Optimize query key with debounce
  const queryKey = useMemo(() => {
    const key = [
      'storesMap',
      Math.round(bounds.sw.lat * 100) / 100,
      Math.round(bounds.sw.lng * 100) / 100,
      Math.round(bounds.ne.lat * 100) / 100,
      Math.round(bounds.ne.lng * 100) / 100
    ];

    const keyString = JSON.stringify(key);
    const boundsChanged = keyString !== lastBoundsRef.current;
    if (boundsChanged) {
      lastBoundsRef.current = keyString;
    }

    return key;
  }, [bounds.sw.lat, bounds.sw.lng, bounds.ne.lat, bounds.ne.lng]);

  const {
    data: storesData,
    isLoading,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey,
    queryFn: () => debouncedFetch(queryKey, async () => {
      // استدعاء API حقيقي لجلب المتاجر
      const {StoreRepositoryImpl} = await import('../repository/StoreRepositoryImpl');
      const storeRepository = new StoreRepositoryImpl();
      const stores = await storeRepository.getStoresForMap(bounds);

      return stores;
    }),
    enabled: shouldFetch && initialized && isAuthenticated && !isProvider, // Enable when shouldFetch is true, user is authenticated and not a provider
    placeholderData: (previousData) => previousData,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Don't retry on 429 (Too Many Requests) errors
      if (error?.response?.status === 429) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchInterval: false
  }) as { data: Store[], isLoading: boolean, error: any, refetch: any, isFetching: boolean };

  // Update loading state
  useEffect(() => {
    const shouldShowLoading = isLoading && !storesData && stores.length === 0;
    setLoading(shouldShowLoading);
  }, [isLoading, storesData, stores.length, setLoading]);

  // Update error state
  useEffect(() => {
    if (error) {
      setError(error instanceof Error ? error.message : 'حدث خطأ في جلب المتاجر');
    } else {
      setError(undefined);
    }
  }, [error, setError]);

  // Update stores
  useEffect(() => {
    if (storesData) {
      setStores(storesData);
    }
  }, [storesData, setStores]);


  // Cleanup
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const result = {
    stores: storesData || [],
    isLoading: isLoading && !storesData,
    isFetching,
    error: error || mapError,
    refetch
  };


  return result;
};
